import { collectionCardTemplate } from './collectionCardTemplate';
import type { ElementTreeDto, ElementNodeDto } from './schemas/element.schema';

/** One style value: a literal, or a token the store's palette answers. */
type CardStyle = Record<string, string | { token: string }>;

// Builds one part of a card, its id under the card's prefix and its look given for every width.
const part = (prefix: string, suffix: string, type: ElementNodeDto['type'], props: Record<string, unknown>, styles: CardStyle): ElementNodeDto => ({
  id: `${prefix}-${suffix}`, type, props, styles: { base: { default: styles } }, children: []
});

// Builds one badge in the top corner, shown only while its binding has words, on the colour named for it.
function badge(prefix: string, kind: 'sale' | 'new' | 'soldout', source: 'product.saleBadge' | 'product.newBadge' | 'product.soldOutBadge', color: string): ElementNodeDto {
  const node = part(prefix, `badge-${kind}`, 'paragraph', { text: '' }, {
    backgroundColor: { token: `colors.${color}` }, color: { token: 'colors.onPrimary' }, fontSize: '13px', fontWeight: '600', marginTop: '0px', marginBottom: '0px'
  });
  node.bindings = { text: source };
  node.cssClass = 'xe-pcard-badge';
  return node;
}

// Builds the picture: a link to the product holding its first photo and the second one a hover fades in.
function cardMedia(prefix: string): ElementNodeDto {
  const frame = part(prefix, 'frame', 'block-link', { href: '', label: '' }, { position: 'relative', display: 'block', aspectRatio: '1/1', overflow: 'hidden' });
  frame.bindings = { href: 'product.href', label: 'product.title' };
  frame.cssClass = 'xe-pcard-media';
  const image = part(prefix, 'image', 'image', { src: '', alt: 'Product' }, { width: '100%', height: '100%', objectFit: 'cover' });
  image.bindings = { src: 'product.image', alt: 'product.title' };
  image.cssClass = 'xe-cat-product-image';
  const hover = part(prefix, 'hover', 'image', { src: '', alt: '' }, { width: '100%', height: '100%', objectFit: 'cover' });
  hover.bindings = { src: 'product.hoverImage' };
  hover.cssClass = 'xe-cat-product-image xe-cat-product-image--second';
  frame.children = [image, hover];
  return frame;
}

// Builds the words under the picture: the type, the name linking to the product, and the price.
function cardInfo(prefix: string): ElementNodeDto {
  const info = part(prefix, 'info', 'container', {}, { display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '20px', paddingBottom: '24px', paddingLeft: '24px', paddingRight: '24px' });
  const kind = part(prefix, 'type', 'paragraph', { text: '' }, { fontSize: '14px', color: { token: 'colors.textMuted' }, marginTop: '0px', marginBottom: '0px' });
  kind.bindings = { text: 'product.type' };
  const title = part(prefix, 'title', 'link', { label: '', href: '' }, { fontSize: '18px', fontWeight: '500', lineHeight: '1.4', color: { token: 'colors.heading' }, textDecoration: 'none' });
  title.bindings = { label: 'product.title', href: 'product.href' };
  const prices = part(prefix, 'prices', 'container', { productPrice: true }, { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'baseline' });
  prices.cssClass = 'xe-pcard-price';
  const now = part(prefix, 'price', 'paragraph', { text: '' }, { fontSize: '16px', fontWeight: '500', color: { token: 'colors.heading' }, marginTop: '0px', marginBottom: '0px' });
  now.bindings = { text: 'product.price' };
  now.cssClass = 'xe-pcard-now';
  const was = part(prefix, 'compare', 'paragraph', { text: '' }, { fontSize: '16px', textDecoration: 'line-through', color: { token: 'colors.textMuted' }, marginTop: '0px', marginBottom: '0px' });
  was.bindings = { text: 'product.compareAtPrice' };
  was.cssClass = 'xe-pcard-was';
  prices.children = [now, was];
  info.children = [kind, title, prices];
  // A phone's two-up grid gives each card half the width, so the words sit closer and run smaller.
  info.styles.mobile = { default: { paddingTop: '14px', paddingBottom: '16px', paddingLeft: '14px', paddingRight: '14px', gap: '4px' } };
  title.styles.mobile = { default: { fontSize: '15px' } };
  return info;
}

// Builds Sleek's product card from real elements: picture, badges, a quick-add pill, then type, name and price.
export function productCardTemplate(prefix: string): ElementNodeDto[] {
  const card = part(prefix, 'card', 'container', {}, {
    position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
    backgroundColor: { token: 'colors.card' }, borderTopLeftRadius: { token: 'radius.lg' }, borderTopRightRadius: { token: 'radius.lg' },
    borderBottomLeftRadius: { token: 'radius.lg' }, borderBottomRightRadius: { token: 'radius.lg' }
  });
  card.cssClass = 'xe-pcard';
  const badges = part(prefix, 'badges', 'container', {}, { display: 'flex', gap: '6px' });
  badges.cssClass = 'xe-pcard-badges';
  badges.children = [badge(prefix, 'sale', 'product.saleBadge', 'sale'), badge(prefix, 'new', 'product.newBadge', 'badgeNew'), badge(prefix, 'soldout', 'product.soldOutBadge', 'badgeSoldOut')];
  const buy = part(prefix, 'buy', 'purchase', { label: 'Add to cart', quickAdd: true, quickView: true }, {
    height: '48px', backgroundColor: { token: 'colors.surface' }, color: { token: 'colors.heading' }, fontSize: '16px', fontWeight: '500',
    borderTopLeftRadius: { token: 'radius.pill' }, borderTopRightRadius: { token: 'radius.pill' }, borderBottomLeftRadius: { token: 'radius.pill' }, borderBottomRightRadius: { token: 'radius.pill' },
    borderWidth: '0px', cursor: 'pointer'
  });
  buy.cssClass = 'xe-pcard-atc';
  // Half a phone's width is too narrow for the desktop pill, so there it is shorter and its words smaller.
  buy.styles.mobile = { default: { height: '36px', fontSize: '13px' } };
  card.children = [cardMedia(prefix), badges, buy, cardInfo(prefix)];
  return [card];
}

// Reports whether a node draws a card's price, which a grid set to hide prices leaves out.
const isPricePart = (node: ElementNodeDto): boolean =>
  Boolean(node.props.productPrice) || Object.values(node.bindings ?? {}).some((source) => source === 'product.price' || source === 'product.compareAtPrice');

// Removes a card's price parts at any depth, for a grid set to hide prices.
export const withoutPriceParts = (nodes: readonly ElementNodeDto[]): ElementNodeDto[] =>
  nodes.filter((node) => !isPricePart(node)).map((node) => ({ ...node, children: withoutPriceParts(node.children) }));

// Reports whether a node is a card's picture frame that is not yet a link: a container holding the product's image.
const isPlainProductFrame = (node: ElementNodeDto): boolean =>
  node.type === 'container' && node.children.some((child) => child.type === 'image' && child.bindings?.src === 'product.image');

// Turns a card's picture frame into a link to its product, keeping everything inside it and how it looks.
const asProductLink = (node: ElementNodeDto): ElementNodeDto => ({
  ...node, type: 'block-link', props: { href: '', label: '', ...node.props },
  bindings: { ...node.bindings, href: 'product.href', label: 'product.title' }
});

// Upgrade older grids on read without changing any already-authored card children.
export function withEditableProductCards(tree: ElementTreeDto): ElementTreeDto {
  const used = new Set<string>();
  const collect = (node: ElementNodeDto) => { used.add(node.id); node.children.forEach(collect); };
  tree.roots.forEach(collect);
  const visit = (node: ElementNodeDto, inList = false): ElementNodeDto => {
    // Early product-card links stored their visible words as `text`, while the Link inspector owns the
    // field as `label`. The storefront could draw that alias, but the editor therefore showed an
    // unrelated “Read more” input instead of the locked Product title source. Normalize it on every
    // read so installed stores gain the proper editable element without rewriting their saved page.
    const titleBinding = node.type === 'link' && node.bindings?.text === 'product.title';
    const normalized: ElementNodeDto = (() => {
      if (!titleBinding) return node;
      const { text: _legacyText, ...bindings } = node.bindings ?? {};
      return { ...node, props: { ...node.props, label: '' }, bindings: { ...bindings, label: 'product.title' } };
    })();
    if (['product-list', 'collection-list'].includes(normalized.type) && normalized.children.length === 0) {
      const template = normalized.type === 'collection-list' ? collectionCardTemplate : productCardTemplate;
      let prefix = `${normalized.id}-card`;
      const overlaps = (node: ElementNodeDto): boolean => used.has(node.id) || node.children.some(overlaps);
      while (template(prefix).some(overlaps)) prefix += '-new';
      const children = template(prefix);
      children.forEach(collect);
      return { ...normalized, children };
    }
    // Cards saved before their picture became a link gain it on read, inside product lists only.
    const linked = inList && isPlainProductFrame(normalized) ? asProductLink(normalized) : normalized;
    const childInList = inList || normalized.type === 'product-list';
    return { ...linked, children: linked.children.map((child) => visit(child, childInList)) };
  };
  return { ...tree, roots: tree.roots.map((root) => visit(root)) };
}
